/** @format */

import { useParams, Link, useNavigate } from "react-router-dom";
import type { ImportJob } from "@question-bank/shared";
import { Locale } from "../features/i18n/init";
import { ImportStatusBadge } from "./ImportStatusBadge";

interface CsvImportManagementProps {
	/** Import history, newest first */
	jobs: ImportJob[];
}

const summaryStyles = "flex flex-col gap-2 p-4 rounded-lg w-full border";
const countStyles = "font-bold text-[30px]";
const textCountStyles = "font-base";
const headerCellStyles = "px-3 py-2";
const cellStyles = "px-4 py-3 text-slate-700";

export const CsvImportManagement = ({ jobs }: CsvImportManagementProps) => {
	const { locale } = useParams<{ locale: Locale }>();
	const navigate = useNavigate();
	const jobPath = (id: string) => `/${locale}/admin/import-jobs/${encodeURIComponent(id)}`;

	const completedCount = jobs.filter((job) => job.status === "complete").length;
	const pendingCount = jobs.filter((job) => job.status === "pending").length;

	return (
		<div>
			<div className="flex flex-row gap-4 items-center justify-between mb-6">
				<div className={`${summaryStyles} bg-gray-100 border-gray-100 text-gray-800`}>
					<p className={countStyles}>{jobs.length}</p>
					<p className={textCountStyles}>Total imports</p>
				</div>
				<div className={`${summaryStyles}  bg-green-100 border-green-100 text-green-800`}>
					<p className={countStyles}>{completedCount}</p>
					<p className={textCountStyles}>Completed imports</p>
				</div>
				<div className={`${summaryStyles} bg-amber-100 border-amber-100 text-amber-800`}>
					<p className={countStyles}>{pendingCount}</p>
					<p className={textCountStyles}>Pending imports</p>
				</div>
			</div>

			{jobs.length === 0 ? (
				<p className="text-sm text-slate-600">No imports yet. Import a CSV to see its job here.</p>
			) : (
				<div className="overflow-x-auto overflow-y-auto max-h-96 rounded-lg border border-slate-200 bg-white shadow-(--admin-shadow-raised)">
					<table className="min-w-full divide-y divide-slate-200 text-left text-sm">
						<thead className="bg-slate-50 text-xs font-semibold uppercase tracking-[0.08em] text-slate-500">
							<tr>
								<th scope="col" className={headerCellStyles}>
									subject
								</th>
								<th scope="col" className={headerCellStyles}>
									paper code
								</th>
								<th scope="col" className={headerCellStyles}>
									author
								</th>
								<th scope="col" className={headerCellStyles}>
									import date
								</th>
								<th scope="col" className={headerCellStyles}>
									status
								</th>
								<th scope="col" className={headerCellStyles}>
									progress
								</th>
							</tr>
						</thead>
						<tbody>
							{jobs.map((job) => (
								<tr
									key={job.id}
									onClick={() => navigate(jobPath(job.id))}
									className="cursor-pointer border-b border-slate-100 text-left transition-colors hover:bg-slate-50"
								>
									<td className={cellStyles}>
										<Link
											to={jobPath(job.id)}
											onClick={(event) => event.stopPropagation()}
											className="font-semibold text-(--admin-color-primary) hover:underline"
										>
											{job.subject ?? job.fileName ?? (job.source === "pdf" ? "PDF import" : job.id)}
										</Link>
									</td>
									<td className={cellStyles}>{job.paperCode ?? "-"}</td>
									<td className={cellStyles}>{job.createdByEmail ?? job.createdBy}</td>
									<td className={cellStyles}>{new Date(job.createdAt).toLocaleDateString("en-GB")}</td>
									<td className={cellStyles}>
										<ImportStatusBadge status={job.status} failedReason={job.failedReason} />
									</td>
									<td className={cellStyles}>
										{job.counts
											? `Extracted ${job.counts.extracted} · Accepted ${job.counts.accepted} · Staged ${job.counts.staged} · Published ${job.counts.published}`
											: "-"}
									</td>
								</tr>
							))}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
};
